"use client";

/* eslint-disable no-param-reassign */
import AmpService from "analytics/amplitude/amplitudeEvents";
import classnames from "classnames";
import { FILTER_TYPE } from "constant/product-filter.action";
import { RootState } from "interfaces/generics.type";
import mergeOptions from "merge-options";
import { useRouter } from "next/router";
import qs from "query-string";
import { useSelector } from "react-redux";
import { ChevronRight } from "lucide-react";
import { validQueryParams, WHITELIST_PARAMS } from "../../../modules/category/constants/constants";
import { isEmpty } from "../../../util/lodash/isEmpty";
import { pick } from "../../../util/lodash/pick";
import {
  LINE_PAGES, TIER_MAKE_MODEL_PAGES, TIER_PAGES,
} from "../../../util/product/product-constants";
import { getHrefAndPath } from "../../../util/url/getHrefAndPath";
import FilterLabel from "./FilterLabel";
import ProductFilter from "./product-filter/ProductFilter";
import RenderLink from "./RenderLink";

const CATEGORY_FILTER_TREE_PADDING = 10;

function getHrefAndAsPath(
  {
    localFilterKey,
    tiersWithUrl,
  }: any,
  immutableFilters: any,
  routerQuery: any,
  href: any,
  currentPageType: any,
  selectedVehicle: any,
) {
  const { url, pageType } = tiersWithUrl || {};
  const categoryOrder = [
    FILTER_TYPE.TIER0,
    FILTER_TYPE.TIER1,
    FILTER_TYPE.TIER2,
    FILTER_TYPE.TIER3,
  ];
  let findCategory = false;
  const filtered = categoryOrder.filter((key, innerIndex) => {
    if (innerIndex > 0 && localFilterKey === categoryOrder[innerIndex - 1]) {
      findCategory = true;
    }
    return findCategory && immutableFilters.indexOf(key) === -1;
  });

  let vehicleFilter = {};
  if (
    TIER_MAKE_MODEL_PAGES.includes(currentPageType)
    && routerQuery.year
    && TIER_PAGES.includes(pageType)
  ) {
    vehicleFilter = {
      ...selectedVehicle,
      engine: undefined,
    };
  }
  const queryString = qs.stringify(
    pick(
      {
        ...routerQuery,
        pathname: undefined,
        scroll: undefined,
        ...vehicleFilter,
        // remove unrequired filters form categoryOrder
        ...filtered.reduce((result, val) => {
          result[val] = undefined;
          return result;
        }, {}),
      },
      process.browser ? validQueryParams : WHITELIST_PARAMS,
    ),
  );

  return getHrefAndPath({
    queryString,
    href,
    asPath: url || routerQuery.pathname,
  });
}

function filterSelectedCategories(
  filterKey: any,
  {
    productFilter,
    immutableFilters,
  }: any,
) {
  const categoryOrder = [
    FILTER_TYPE.TIER0,
    FILTER_TYPE.TIER1,
    FILTER_TYPE.TIER2,
    FILTER_TYPE.TIER3,
  ];

  return categoryOrder.filter(
    (categoryFilterKey) => immutableFilters.indexOf(categoryFilterKey) === -1
      && categoryFilterKey !== filterKey
      && !isEmpty(productFilter[categoryFilterKey]),
  );
}

function CategoryHeaderContent(
  filterKey: any,
  {
    immutableFilters,
    productFilter,
    tiersWithUrls = [],
    routerQuery,
    url,
    currentPageType,
    selectedVehicle,
  }: any,
) {
  const filters = filterSelectedCategories(filterKey, {
    productFilter,
    immutableFilters,
  });
  if (!filters.length) {
    return null;
  }

  return filters.map((categoryFilterKey, index) => {
    const renderContent = (actionCaretLeft: any, actionLabel: any) => {
      const { href: labelInfoHref, asPath: labelInfoAsPath } = getHrefAndAsPath(
        actionLabel,
        immutableFilters,
        routerQuery,
        url,
        currentPageType,
        selectedVehicle,
      );
      const { href: carethref, asPath: caretAsPath } = getHrefAndAsPath(
        actionCaretLeft,
        immutableFilters,
        routerQuery,
        url,
        currentPageType,
        selectedVehicle,
      );

      return (
        <>
          {index > 0 && (
            <i
              className="left-line"
              style={{
                left: `${(index - 1) * CATEGORY_FILTER_TREE_PADDING + 5 + 8}px`,
                width: `${CATEGORY_FILTER_TREE_PADDING}px`,
              }}
            />
          )}

          <i
            className="right-line"
            style={{
              left: `${index * CATEGORY_FILTER_TREE_PADDING + 1 + 8}px`,
              width: `${Math.max(index * CATEGORY_FILTER_TREE_PADDING, 5)}px`,
            }}
          />

          <div className="media">

            <div className="media-left">

              <RenderLink
                href={carethref}
                asPath={caretAsPath}
                label={<span className="fa fa-caret-left" title="Go back" />}
                onClick={() => {
                  const categoryFilter = categoryFilterKey.substring(0, categoryFilterKey.length - 1) + (categoryFilterKey.slice(-1) - 1);
                  AmpService.filterCategory(productFilter, categoryFilter);
                }}
              />
            </div>

            <div className={`media-body shade${index}`}>

              <span>

                <RenderLink
                  href={labelInfoHref}
                  asPath={labelInfoAsPath}
                  label={productFilter[categoryFilterKey]}
                  onClick={() => AmpService.filterCategory(productFilter, categoryFilterKey)}
                />
              </span>
            </div>
          </div>
        </>
      );
    };
    const previousTiersWithUrl = tiersWithUrls.filter(
      (item) => item.tier.toLowerCase()
        === productFilter[categoryFilterKey].toLowerCase(),
    );

    if (previousTiersWithUrl && previousTiersWithUrl[0]) {
      return (
        <div
          key={index}
          className={classnames(`category-header cat${index}`, {
            last: index === filters.length - 1,
          })}
          style={{
            paddingLeft: `${index * CATEGORY_FILTER_TREE_PADDING + Math.min(5 * index, 5)
              }px`,
          }}
        >
          {renderContent(
            {
              localFilterKey: index === 0 ? "tier0" : filters[index - 1],
              tiersWithUrl: tiersWithUrls[index],
            },
            {
              localFilterKey: categoryFilterKey,
              tiersWithUrl: previousTiersWithUrl[0],
            },
          )}
        </div>
      );
    }

    return (
      <div
        key={index}
        className={classnames(`category-header cat${index}`, {
          last: index === filters.length - 1,
        })}
        style={{
          paddingLeft: `${index * CATEGORY_FILTER_TREE_PADDING + Math.min(5 * index, 5)
            }px`,
        }}
      >
        {renderContent(
          {
            localFilterKey: index === 0 ? "tier0" : filters[index - 1],
            tiersWithUrl: null,
          },
          {
            localFilterKey: categoryFilterKey,
            tiersWithUrl: null,
          },
        )}
      </div>
    );
  });
}

const FilterLine = ({
  className,
  left,
  width,
}: any) => <i className={className} style={{ left: `${left + 3}px`, width: `${width}px` }} />;

function CategoryLabel(item: any, filterKey: any, {
  productFilter,
  immutableFilters,
}: any) {
  const filters = filterSelectedCategories(filterKey, {
    productFilter,
    immutableFilters,
  });
  const hasNext = filterKey !== FILTER_TYPE.TIER3;
  const categoryPadding = hasNext ? 0 : CATEGORY_FILTER_TREE_PADDING;
  const paddingLeft = (filters.length - 1) * categoryPadding + 5 * filters.length + 5;
  let mediaStyle = {};
  let iconStyle = {};
  const selectedItem = productFilter[filterKey];

  if (hasNext && filters.length > 0) {
    mediaStyle = { marginLeft: 0, marginTop: -2 };
    iconStyle = { marginLeft: -5, marginTop: 0 };
  }

  let active = false;
  if (
    selectedItem
    && item.bucket
    && item.bucket.toLowerCase() === selectedItem.toLowerCase()
  ) {
    active = true;
  }

  return (
    <div
      className={classnames(`d-flex category-item lvl${filters.length}`, {
        active,
      })}
      style={{
        paddingLeft: `${paddingLeft + categoryPadding}px`,
        minHeight: 22,
      }}
    >
      {hasNext && (
        <>

          <FilterLine
            className="top-line"
            left={filters.length > 0 ? paddingLeft : paddingLeft + categoryPadding + 5}
            width={0}
          />

          <FilterLine
            className="bottom-line"
            left={filters.length > 0 ? paddingLeft : paddingLeft + categoryPadding + 5}
            width={filters.length > 0 ? categoryPadding : ""}
          />
        </>
      )}

      <i
        className={classnames("fa p-r-5", {
          "fa-angle-right": !hasNext,
          "fa-plus-square-o": hasNext,
        })}
        style={{ marginTop: 1, ...iconStyle }}
      />

      <div className="bucket-container" style={mediaStyle}>
        {!hasNext && (
          <>

            <FilterLine
              className="left-line"
              left={paddingLeft}
              width={categoryPadding}
            />

            <FilterLine className="bottom-line left-corner" left={paddingLeft} width={0} />
          </>
        )}

        <span className="bucket-label" title={item.bucket}>
          {item.bucket}
        </span>

        <span className="result-count">{` (${item.count})`}</span>
      </div>
      {item.bucket === selectedItem && (
        <div className="media-right">

          <ChevronRight />
        </div>
      )}
    </div>
  );
}

function TierSize(item: any) {
  return (
    <FilterLabel
      item={{
        bucket: `${item.bucket.substr(-2)}" ${item.bucket}`,
        count: item.count,
      }}
    />
  );
}

function SizeUnit(item: any, unit: any) {
  return (
    <FilterLabel
      item={{
        bucket: `${item.bucket}${unit}`,
        count: item.count,
      }}
    />
  );
}

function ProductFilterContainer(props: any) {
  const {
    immutableFilters,
    filterParams,
    filters, //------------
    filterOptions,
    nbcFilterableAttributes = [],
    mobileProps,
    url,
    tiersWithUrls,
    appliedFilters,
    garageTitle,
    categoryAggregation,
    isModal,
    className,
    customDropdown,
    maxFilters,
  } = props;

  const { currentPageType, urlInformation } = useSelector((state: RootState) => state.utils);
  const selectedVehicle = useSelector((state: RootState) => state.garage.selectedVehicle);

  const productFilter = urlInformation;

  const router = useRouter();
  const routerQuery = router.query;

  const shouldRenderVehicle = () => {
    const t0 = urlInformation[FILTER_TYPE.TIER0];
    return !isEmpty(t0) || (
      LINE_PAGES.includes(currentPageType)
    );
  };
  const { fitmentOptions = [], fitments, ...restFilters } = filterOptions;

  let fitmentFilters = {};
  let fitmentParams = {};
  try {
    fitmentFilters = Object.fromEntries(fitmentOptions
      .map((el) => [el.id, el.bucketCounts]));
    fitmentParams = Object.fromEntries(fitmentOptions
      .map((el) => [el.id, {
        type: "checkbox",
        order: 2,
        title: el.title,
        filterKey: el.id,
        icon: el.icon,
        isFitmentAttribute: true,
        multiple: false,
        collapsible: false,
      }]));
  } catch (err) {
    console.log(err);
    // https://partsavatar.sentry.io/issues/4209411543/?query=is%3Aunresolved&referrer=issue-stream&stream_index=7
  }

  // Show Fitment filter only if all filters count is > 0
  const showFitmentFilter = fitments?.length === 2 && fitments.every((filter) => filter.count > 0);
  const nbcFilters = {};
  nbcFilterableAttributes.forEach(({ attributeName, attributeValues }) => {
    nbcFilters[attributeName] = attributeValues.map(({ attributeValue }) => ({
      bucket: attributeValue,
    }));
  });
  return (
    <ProductFilter
      data={{
        ...filters,
        ...fitmentFilters,
        ...nbcFilters,
        ...restFilters,
        fitments: showFitmentFilter ? fitments : [],
        url,
        router,
        showSelectedVehcile: props.showSelectedVehcile,
      }}
      nbcFilterableAttributes={nbcFilterableAttributes}
      customDropdown={customDropdown}
      isMobile={props.isMobile}
      isCategoryOpen={props.isCategoryOpen}
      isModal={isModal}
      categoryAggregation={categoryAggregation}
      params={mergeOptions(
        {},
        { ...fitmentParams },
        {
          tier1: {
            itemLabel: (item: any, filterKey: any) => CategoryLabel(item, filterKey, {
              productFilter,
              immutableFilters,
            }),
            headerContent: (filterKey: any) => CategoryHeaderContent(filterKey, {
              immutableFilters,
              productFilter,
              tiersWithUrls,
              routerQuery,
              url,
              currentPageType,
              selectedVehicle,
            }),
          },
          tier2: {
            itemLabel: (item: any, filterKey: any) => CategoryLabel(item, filterKey, {
              productFilter,
              immutableFilters,
            }),
            headerContent: (filterKey: any) => CategoryHeaderContent(filterKey, {
              immutableFilters,
              productFilter,
              tiersWithUrls,
              routerQuery,
              url,
              currentPageType,
              selectedVehicle,
            }),
          },
          tier3: {
            itemLabel: (item: any, filterKey: any) => CategoryLabel(item, filterKey, {
              productFilter,
              immutableFilters,
            }),
            headerContent: (filterKey: any) => CategoryHeaderContent(filterKey, {
              immutableFilters,
              productFilter,
              tiersWithUrls,
              routerQuery,
              url,
              currentPageType,
              selectedVehicle,
            }),
          },
          sizes: {

            itemLabel: (tem: any, filterKey: any) => TierSize(tem, filterKey),
          },
          diameters: {
            itemLabel: (item: any) => SizeUnit(item, "\""),
          },
          widths: {
            itemLabel: (item: any) => SizeUnit(item, "\""),
          },
          offsets: {
            itemLabel: (item: any) => SizeUnit(item, "mm"),
          },
          vehicle: {
            enabled: shouldRenderVehicle,
          },
        },
        filterParams,
      )}
      tiersWithUrls={tiersWithUrls}
      immutableFilters={immutableFilters}
      appliedFilters={appliedFilters}
      garageTitle={garageTitle}
      {...mobileProps}
      productFilter={productFilter}
      className={className}
      maxFilters={maxFilters}
    />
  );
}

export default ProductFilterContainer;
